import { Voice, body, transient, vary } from '../audio-engine';
/* ============================================================
   配方：悶厚工業 ← **使用者 2026-08-05 選定**
   ============================================================

   ## 為什麼是這一版

   最早的「撞擊優先・極簡」被否決：**不好聽，而且很躁**。病因不是數值是
   **成分** —— 三個音的瞬態都是白噪且集中在 2–5 kHz，那是耳朵最敏感也最
   容易疲勞的頻段，而命中每秒響十幾次。只要高頻事件的瞬態還是噪音，
   怎麼調都會刺。

   之後做了兩個候選（本檔與 `tonal.ts`），使用者選了這一個。

   ## 這一版的規則

   1. **能量全部往下移。** 瞬態一律**低通**（「thock」不是「tss」），
      濾波點最高只掃到 1.8 kHz，而且那只有爆炸用得到。
   2. **命中完全不用噪音**：一個 22 ms 的低音點擊。
      一秒響十幾次的東西不能有任何嘶聲成分。
   3. **辨識度靠三件事：落點多低、拖多久、幾下。**
      音色全部悶著，所以區辨不能靠明暗 —— 靠的是低頻的深度、
      尾巴的長度、以及「這是一下還是兩下」。

   ## 全套的語法（不是一堆孤立的音）

   | 語法 | 意思 | 用在哪 |
   |---|---|---|
   | 音高**往下**掉 | 撞擊、結束 | 主砲、擊殺、爆炸 |
   | 音高**往上**推 | 好事、起來 | 升級、進化前置、復活、傳送門 |
   | **兩下以上** | 齊射、警告 | 飛彈（兩下）、警報（兩下）、進化前置（三下） |
   | **拖得最久** | 這是大事 | 大招 0.7 秒、復活 0.7 秒 |
   | **滑不停的音高** | 不對勁 | 受傷（唯一一個音高一路滑到底的） |

   聽起來像重機械、氣壓、閘門。連續聽一小時不會累，
   而且跟硬邊工業機甲的題材是同一種語言。 */

/** 排一個延遲的音。**不吃 `timeScale`** —— 音效不該跟著頓幀慢下來
    （頓幀是視覺的節拍，聲音慢下來會讀成掉幀）。 */
function at(ms: number, fn: () => void) { setTimeout(fn, ms); }

/* ---------- 主武器（最高頻的三個） ---------- */

/** 主砲：氣壓式的悶響。**沒有任何 2 kHz 以上的東西。** */
export function shoot(v: Voice) {
  transient({
    bus: 'weapon', x: v.x, z: v.z, vol: 0.9, type: 'lowpass', q: 0.8,
    dur: vary(0.010, 0.2), f0: vary(900, 0.12), f1: vary(260, 0.12)
  });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.9, dur: vary(0.07, 0.15), f0: vary(150, 0.08), f1: 55, type: 'triangle' });
}

/** 命中：一個**低音點擊**，零噪音。

    這是整版最重要的一個決定。命中一秒響十幾次，任何嘶聲成分累積起來
    就是「躁」的來源 —— 改成純音之後，密度再高也只是變厚不會變刺。 */
export function hit(v: Voice) {
  /* `imp` = Boss／精英／當前鎖定目標。雜兵的命中已經被密度層聚合掉了
     （`audio-density.ts`），還會單獨響的只有玩家真正在盯的那一隻 ——
     所以它可以放心地比舊版更清楚一點。 */
  var im = v.imp ? 1 : 0;
  body({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.5 + im * 0.22,
    dur: vary(0.022 + im * 0.010, 0.25), f0: vary(430 + im * 90, 0.12), f1: 190, type: 'sine', drop: 0.35
  });
}

/** 擊殺：閘門關上。瞬態放寬到 1.4 kHz（仍然是低通），本體掉到 38 Hz。

    **`v.n` 是這個音代表幾隻**（密度層聚合的結果）。數量一律用**質地**表達，
    絕對不要用音量 —— 「殺得多所以更大聲」會直接把低頻堆回去，
    那正是 2026-08-06「難受」的病因。所以 n 越大：
    瞬態越寬越碎（高頻成分多＝聽起來是一片而不是一下）、
    本體反而**略短**（爆量的時候節奏要快，拖長只會糊）。 */
export function kill(v: Voice) {
  var n = Math.max(1, v.n || 1);
  var k = Math.min(1, (n - 1) / 6);      // 0 = 單隻，1 = 七隻以上
  var im = v.imp ? 1 : 0;                // Boss／精英／鎖定目標：值得單獨聽見
  transient({
    bus: 'enemy', x: v.x, z: v.z, vol: 0.85 + k * 0.10 + im * 0.10, type: 'lowpass', q: 0.6 - k * 0.18,
    dur: vary(0.018 + k * 0.012, 0.2), f0: vary(1400 + k * 900, 0.15), f1: vary(230, 0.15)
  });
  body({
    bus: 'enemy', x: v.x, z: v.z, vol: 1.0, dur: vary(0.19 - k * 0.05 + im * 0.06, 0.12),
    f0: vary(120, 0.1), f1: 38, type: 'sine', drop: 0.4
  });
  /* 三隻以上補一顆錯開的碎瞬態。「一片」與「一隻」的差別是**層次**，
     不是響度 —— 這一顆才是讓爆量聽起來有份量的東西。 */
  if (n >= 3) at(18, function () {
    transient({
      bus: 'enemy', x: v.x, z: v.z, vol: 0.35 + k * 0.28, type: 'lowpass', q: 0.5,
      dur: 0.014, f0: vary(1100, 0.2), f1: 260
    });
  });
}

/* ---------- 副武器與大招 ---------- */

/** 飛彈齊射：**兩下**。齊射就是齊射 —— 一下讀成單發，兩下才讀成「一排」。 */
export function missile(v: Voice) {
  transient({ bus: 'weapon', x: v.x, z: v.z, vol: 0.7, type: 'lowpass', q: 0.7, dur: 0.012, f0: vary(1100, 0.1), f1: 300 });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.8, dur: 0.11, f0: vary(130, 0.08), f1: 48, type: 'triangle' });
  at(72, function () {
    body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.6, dur: 0.13, f0: vary(110, 0.08), f1: 42, type: 'triangle' });
  });
}

/** 大招：**全套最長、最低的一個**（0.7 秒）。

    這一版沒有音高可以拿來做「特別」，所以「大」只能用時間與深度表達 ——
    兩層低頻疊著慢慢掉，加一個比別人都寬的低通瞬態。 */
export function beam(v: Voice) {
  transient({ bus: 'weapon', x: v.x, z: v.z, vol: 0.9, type: 'lowpass', q: 0.5, dur: 0.026, f0: 1600, f1: 240 });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 1.0, dur: 0.70, f0: 92, f1: 36, type: 'sine', drop: 0.3 });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.5, dur: 0.50, f0: 138, f1: 54, type: 'triangle', drop: 0.35 });
}

/** 斬擊：全套最短的一下。悶著但很快 —— 快就是刃。 */
export function slash(v: Voice) {
  /* 這個音包沒有音階可以爬，所以升階（`v.step`）走**份量**：
     更響、更低、尾巴更長。悶厚的美學裡「重」才是升級，「高」是變薄。 */
  var d = Math.max(0, Math.min(2, v.step || 0));
  transient({ bus: 'weapon', x: v.x, z: v.z, vol: 0.65 + d * 0.16, type: 'lowpass', q: 0.9,
    dur: 0.008 + d * 0.004, f0: vary(1600 - d * 220, 0.12), f1: 420 });
  body({ bus: 'weapon', x: v.x, z: v.z, vol: 0.45 + d * 0.20, dur: 0.04 + d * 0.03,
    f0: vary(260 - d * 40, 0.1), f1: 120, type: 'triangle' });
}

/* ---------- 玩家的動作與狀態 ---------- */

/** 衝刺：一口氣。極輕、極悶，只有一層。 */
export function dash(v: Voice) {
  transient({ bus: 'ui', x: v.x, z: v.z, vol: 0.42, type: 'lowpass', q: 0.6, dur: 0.016, f0: 620, f1: 210 });
}

/** 受傷：**唯一一個音高一路滑到底的音**（`drop: 1`）。

    這一版沒有音階可以用「不協和」標示壞消息，所以改用**行為** ——
    其他每個音的音高都是先掉到底再讓尾巴衰減（穩定的低音），
    只有這個是一路往下滑到最後一刻。滑不停 = 沒站穩 = 不對勁。 */
export function hurt(v: Voice) {
  transient({ bus: 'ui', x: v.x, z: v.z, vol: 0.8, type: 'lowpass', q: 1.4, dur: 0.02, f0: 780, f1: 180 });
  body({ bus: 'ui', x: v.x, z: v.z, vol: 0.95, dur: 0.26, f0: 190, f1: 44, type: 'triangle', drop: 1 });
}

/** 復活：**往上推**的低頻（`f0 < f1`）＋ 一個很悶的起音。全套第二長。 */
export function revive(v: Voice) {
  transient({ bus: 'ui', x: v.x, z: v.z, vol: 0.5, type: 'lowpass', q: 0.6, dur: 0.03, f0: 500, f1: 200 });
  body({ bus: 'ui', x: v.x, z: v.z, vol: 0.9, dur: 0.75, f0: 58, f1: 132, type: 'sine', drop: 0.85 });
}

/* ---------- 事件與介面 ---------- */

/** 爆炸：最重的一個。瞬態最寬（1.8 kHz，全套唯一過 1.6 的），本體掉到 28 Hz。 */
export function boom(v: Voice) {
  transient({ bus: 'world', x: v.x, z: v.z, vol: 0.95, type: 'lowpass', q: 0.5, dur: 0.024, f0: vary(1800, 0.12), f1: 200 });
  body({ bus: 'world', x: v.x, z: v.z, vol: 1.0, dur: 0.32, f0: vary(95, 0.08), f1: 28, type: 'sine', drop: 0.32 });
}

/** 升級：**兩下往上推**。上行＝好事，這是全套的基本語法。 */
export function level(v: Voice) {
  body({ bus: 'ui', vol: 0.7, dur: 0.16, f0: 105, f1: 160, type: 'triangle', drop: 0.7 });
  at(100, function () {
    body({ bus: 'ui', vol: 0.75, dur: 0.26, f0: 150, f1: 235, type: 'triangle', drop: 0.7 });
  });
}

/** 進化前置達成：**三下**往上推。比升級多一下 ——
    這一版沒有音色可以拿來分級，階差只能用「幾下」表達。 */
export function preEvo(v: Voice) {
  var F = [[100, 150], [140, 205], [190, 290]];
  for (var i = 0; i < F.length; i++) {
    (function (f, k) {
      at(k * 82, function () {
        body({ bus: 'ui', vol: 0.62, dur: k === 2 ? 0.3 : 0.15, f0: f[0], f1: f[1], type: 'triangle', drop: 0.7 });
      });
    })(F[i], i);
  }
}

/** 傳送門：最慢的一次上推，而且幾乎沒有起音 —— 它是「開了一個東西」
    不是「打了一下」。 */
export function portal(v: Voice) {
  body({ bus: 'world', vol: 0.7, dur: 0.85, f0: 62, f1: 104, type: 'sine', drop: 0.9 });
  body({ bus: 'world', vol: 0.35, dur: 0.7, f0: 124, f1: 208, type: 'triangle', drop: 0.9 });
}

/** 警報：**兩下一模一樣的悶響**。

    重複是警報的語法（單次讀成「某件事發生了」，重複才讀成「注意」）。
    這一版不能用不協和音程，所以第二下刻意**比第一下低**一階 ——
    往下的重複讀成壓迫，往上的重複會讀成好消息。 */
export function alarm(v: Voice) {
  for (var i = 0; i < 2; i++) {
    (function (k) {
      at(k * 215, function () {
        transient({ bus: 'ui', vol: 0.6, type: 'lowpass', q: 1.6, dur: 0.02, f0: 900, f1: 260 });
        body({ bus: 'ui', vol: 0.8, dur: 0.2, f0: k ? 118 : 152, f1: k ? 46 : 60, type: 'triangle', drop: 0.45 });
      });
    })(i);
  }
}
